import { z } from 'zod';
import { getDashboardModalExtensionsDir } from '@wix/cli-astro-definitions';
import {
  VALID_PATH_REGEX,
  folderExistsMessage,
  pathAvailableValidation,
} from '../../../dev-center/components/validations/common.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { enrichWithDerivedFolder } from '../utils/derive-folder.js';
import type { WithDerivedFolder } from '../utils/derive-folder.js';
import { generateDashboardModal } from './generate.js';

const schema = z.object({
  extensionType: z.literal('DASHBOARD_MODAL'),
  title: z.string().describe(defineField({ title: 'Modal title' })),
  folder: z
    .string()
    .regex(VALID_PATH_REGEX)
    .optional()
    .describe(
      defineField({
        title: 'Modal folder',
        description:
          'Lowercase alphanumeric and hyphens only. Derived from the modal title when omitted.',
      })
    ),
});

export const dashboardModal: ExtensionDef<
  typeof schema,
  WithDerivedFolder<typeof schema>
> = {
  title: 'Dashboard Modal',
  description: 'A Dashboard modal dialog',
  group: 'Dashboard',
  documentation:
    'https://dev.wix.com/docs/wix-cli/guides/extensions/dashboard-extensions/dashboard-modals/add-dashboard-modal-extensions',
  zodSchema: schema,
  enrich: (data) => enrichWithDerivedFolder(data, { sourceField: 'title' }),
  validate: async (projectFolder, data) => {
    const isAvailable = pathAvailableValidation(
      getDashboardModalExtensionsDir(projectFolder)
    )(data.folder);
    return isAvailable
      ? null
      : folderExistsMessage('modal', data.folder, 'title');
  },
  generate: ({ projectFolder, data }) =>
    generateDashboardModal(projectFolder, data.folder, data.title),
};
